import { Link } from "react-router-dom";
import PublicPageShell, { BRAND, PageTitle } from "./PublicPageShell";
import { usePlans, subscriptionTotalMinor, nextSeatMinor } from "../../hooks/usePlanPricing";
import { formatAudMinor } from "../../utils/seatPricing";

/**
 * Public pricing, at /pricing.
 *
 * <p><b>Every figure is read from Stripe.</b> `GET /api/stripe/plans` is anonymous, so this page
 * prices itself from the same live catalogue the signed-in plans page uses, through the same
 * `subscriptionTotalMinor` — the client mirror of SubscriptionShape.cs. Nothing is typed into this
 * file. A public pricing page carrying its own copy of the numbers is the worst place for them to
 * drift, because it is the page a customer quotes back at you.</p>
 *
 * <p>When the catalogue cannot be loaded the page says so and shows no prices at all. "No price"
 * is the only safe fallback; a stale hardcoded figure is how somebody is quoted one number and
 * charged another.</p>
 */

const MARKETPLACE_INCLUDES = [
  "A verified listing in the marketplace",
  "Unlimited enquiries, straight to you",
  "Project gallery on your profile",
];

const INTELLIGENCE_INCLUDES = [
  "Everything in Marketplace",
  "Unlimited councils, suburbs and categories",
  "Matched DA alerts by email and SMS",
  "Development map and saved views",
  "Status-change tracking and documents",
  "CSV export",
];

function Tick({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 15, lineHeight: 1.55, marginBottom: 9 }}>
      <i className="ph-fill ph-check-circle" style={{ color: BRAND, fontSize: 17, marginTop: 1, flex: "none" }} />
      <span style={{ color: "#52606D" }}>{children}</span>
    </li>
  );
}

export default function PartnerNetworkPricing() {
  const plans = usePlans();

  // Null until the catalogue lands, and null for good if it cannot be reached.
  const marketplaceMinor = subscriptionTotalMinor(plans, "marketplace", 1);
  const intelligenceMinor = subscriptionTotalMinor(plans, "intelligence", 1);
  const seatMinor = nextSeatMinor(plans, 1);
  const featuredMinor = subscriptionTotalMinor(plans, "marketplaceFeature", 1);

  const loaded = marketplaceMinor !== null || intelligenceMinor !== null;

  return (
    <PublicPageShell
      title="Pricing"
      description="What it costs to list your business on the DAconnect marketplace and to watch development applications."
      maxWidth={1000}
    >
      <PageTitle
        title="Pricing"
        intro="Flat monthly pricing. No pay-per-lead, no commission on work you win, and no lock-in — change or cancel whenever you like."
      />

      {!loaded && (
        // Not a spinner and not a zero. The page is useless without real prices, and inventing one
        // to fill the gap is the failure this whole approach exists to prevent.
        <div
          data-testid="pricing-unavailable"
          style={{
            background: "#F7F5F1",
            border: "1px solid #E4DED3",
            borderRadius: 12,
            padding: "20px 22px",
            fontSize: 15,
            lineHeight: 1.6,
            color: "#52606D",
          }}
        >
          We can't load current pricing just now. Please refresh, or{" "}
          <Link to="/contact" style={{ color: BRAND, fontWeight: 700 }}>get in touch</Link>{" "}
          and we'll tell you exactly what it costs.
        </div>
      )}

      {loaded && (
        <>
          <div
            className="pn-cat-grid"
            data-testid="pricing-plans"
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, alignItems: "start" }}
          >
            {/* Marketplace */}
            <section
              style={{
                background: "#fff",
                border: "1px solid #E9E5DE",
                borderRadius: 18,
                padding: 28,
              }}
            >
              <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 20, margin: "0 0 4px", color: "#243139" }}>
                Marketplace
              </h2>
              <p style={{ fontSize: 14.5, color: "#6A7484", margin: "0 0 18px" }}>
                Be found by owners and developers.
              </p>
              <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 40, color: "#243139", marginBottom: 20 }}>
                {marketplaceMinor === null ? "—" : formatAudMinor(marketplaceMinor)}
                <span style={{ fontSize: 15, fontWeight: 600, color: "#6A7484" }}> /month</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px" }}>
                {MARKETPLACE_INCLUDES.map((f) => <Tick key={f}>{f}</Tick>)}
              </ul>
              <Link
                to="/signup"
                style={{
                  display: "block",
                  textAlign: "center",
                  textDecoration: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  color: BRAND,
                  border: "1.5px solid #CDE8DB",
                  borderRadius: 10,
                  padding: "13px 18px",
                }}
              >
                List your business
              </Link>
            </section>

            {/* Intelligence */}
            <section
              style={{
                background: "#18272C",
                border: "1px solid #18272C",
                borderRadius: 18,
                padding: 28,
                color: "#fff",
              }}
            >
              <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 20, margin: "0 0 4px", color: "#fff" }}>
                Intelligence
              </h2>
              <p style={{ fontSize: 14.5, color: "#A8B2C2", margin: "0 0 18px" }}>
                Everything in Marketplace, plus the DA engine.
              </p>
              <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 40, marginBottom: 4 }}>
                {intelligenceMinor === null ? "—" : formatAudMinor(intelligenceMinor)}
                <span style={{ fontSize: 15, fontWeight: 600, color: "#A8B2C2" }}> /month</span>
              </div>
              <p style={{ fontSize: 14, color: "#A8B2C2", margin: "0 0 18px" }}>
                {/* Derived, never typed. On the live catalogue this is a flat per-user price; if it
                    ever becomes tiered again, this line changes with it. */}
                {seatMinor === null
                  ? "Includes one user."
                  : `Includes one user · ${formatAudMinor(seatMinor)}/month for each extra person.`}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px" }}>
                {INTELLIGENCE_INCLUDES.map((f) => (
                  <li key={f} style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 15, lineHeight: 1.55, marginBottom: 9 }}>
                    <i className="ph-fill ph-check-circle" style={{ color: "#7FD1A8", fontSize: 17, marginTop: 1, flex: "none" }} />
                    <span style={{ color: "#D7DEE7" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                style={{
                  display: "block",
                  textAlign: "center",
                  textDecoration: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#18272C",
                  background: "#9BE3BE",
                  borderRadius: 10,
                  padding: "13px 18px",
                }}
              >
                Get started
              </Link>
            </section>
          </div>

          {/* Add-ons */}
          <section
            data-testid="pricing-addons"
            style={{
              marginTop: 26,
              background: "#F7F5F1",
              border: "1px solid #E4DED3",
              borderRadius: 16,
              padding: "22px 24px",
            }}
          >
            <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 17, margin: "0 0 14px", color: "#243139" }}>
              Add-ons
            </h2>
            <dl style={{ margin: 0, display: "grid", gap: 12, fontSize: 15 }}>
              {seatMinor !== null && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
                  <dt style={{ color: "#52606D" }}>Extra user on Intelligence</dt>
                  <dd style={{ margin: 0, fontWeight: 700, color: "#243139", whiteSpace: "nowrap" }}>
                    {formatAudMinor(seatMinor)} /month each
                  </dd>
                </div>
              )}
              {featuredMinor !== null && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 18 }}>
                  <dt style={{ color: "#52606D" }}>
                    Featured on the marketplace — top of the page for a week, ten businesses at a time
                  </dt>
                  <dd style={{ margin: 0, fontWeight: 700, color: "#243139", whiteSpace: "nowrap" }}>
                    {formatAudMinor(featuredMinor)} /week
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section style={{ marginTop: 26, fontSize: 15, lineHeight: 1.7, color: "#52606D" }}>
            <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 17, margin: "0 0 10px", color: "#243139" }}>
              How changes are billed
            </h2>
            {/* Describes what the code actually does — see SubscriptionShape and the change-plan
                path. Worth stating publicly because "what happens if I add someone mid-month" is
                the question people ask before signing up, not after. */}
            <p style={{ margin: "0 0 10px" }}>
              Add a user part-way through a month and you're charged only for the days left in it.
              From the next invoice on, it's the full monthly rate.
            </p>
            <p style={{ margin: "0 0 10px" }}>
              Remove someone, or move to a cheaper plan, and the change takes effect at the end of
              the month you've already paid for. You keep what you paid for until then, and we don't
              take it away early.
            </p>
            <p style={{ margin: 0 }}>
              Prices are in Australian dollars. Questions?{" "}
              <Link to="/contact" style={{ color: BRAND, fontWeight: 700 }}>Talk to us</Link>.
            </p>
          </section>
        </>
      )}
    </PublicPageShell>
  );
}
